import { Alert } from "reactstrap";
import { useState, useEffect, useRef } from "react";
import { PdfDocumentViewer } from "../../components/download/PdfDocumentViewer";
import ActivityHistoryTimeLineRow from "./ActivityHistoryTimeLineRow";
import ActivityHistoryHeader from "./ActivityHistoryHeader";
import { LoadingScreen } from "../../components/loader/LoadingScreen";
import EmptyState from "../../components/EmptyState";
import { useTranslation } from "react-i18next";
import useScrollToEnd from "../../hooks/useScrollToEnd";
import { useActivityHistoryData } from "./hooks/useActivityHistoryData";
import { useActivityHistoryExport } from "./hooks/useActivityHistoryExport";
import { useSuperContainers } from "./hooks/useSuperContainers";
import { buildCollectionFluxLookup } from "./utils/collectionExportFlux";
import { Card } from "reactstrap";
import { UI_CONSTANTS } from "../../constants/ui";

const ActivityHistoryTimeLine = ({
  keywordFilter,
  periodHistoryStart,
  periodHistoryStop,
  handleValidate,
  setOpenFilter,
  periodName,
  hasActiveFilters = false,
  onClearFilters,
}) => {
  const materials = JSON.parse(localStorage.getItem("materialsDescription"));
  const { superContainers } = useSuperContainers();
  const { t } = useTranslation();
  const [displayAlert, setDisplayAlert] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const itemsPerPage = UI_CONSTANTS.PAGINATION.ACTIVITY_ITEMS_PER_PAGE;
  const exportDropdownRef = useRef(null);

  useScrollToEnd(() => {
    setCurrentPage((prevPage) => prevPage + 1);
  });

  const { isLoading, dataHistoryContainer, countDataHistory } =
    useActivityHistoryData(
      keywordFilter,
      periodHistoryStart,
      periodHistoryStop,
      false,
      handleValidate,
      setOpenFilter,
    );

  const [resolveHistoryFlux, setResolveHistoryFlux] = useState(null);
  const [isFluxLookupLoading, setIsFluxLookupLoading] = useState(false);

  const {
    downloadProgress,
    progress,
    dataHistoryExport,
    isModalDownload,
    setIsModalDownload,
    disabledDownload,
    exportPDF,
    exportActivityToCSV,
  } = useActivityHistoryExport(
    dataHistoryContainer,
    countDataHistory,
    materials,
    t,
    periodName,
    periodHistoryStart,
    periodHistoryStop,
    keywordFilter,
    resolveHistoryFlux,
  );

  useEffect(() => {
    setResolveHistoryFlux(null);
    setIsFluxLookupLoading(true);
    let cancelled = false;
    const loadFluxLookup = async () => {
      try {
        const lookup = await buildCollectionFluxLookup({
          periodHistoryStart,
          periodHistoryStop,
          keywordFilter,
          superContainers,
        });
        if (!cancelled) {
          setResolveHistoryFlux(() => lookup.resolveForHistoryItem);
        }
      } finally {
        if (!cancelled) {
          setIsFluxLookupLoading(false);
        }
      }
    };
    loadFluxLookup();
    return () => {
      cancelled = true;
    };
  }, [
    periodHistoryStart,
    periodHistoryStop,
    keywordFilter,
    superContainers,
    handleValidate,
  ]);

  useEffect(() => {
    setTimeout(() => {
      if (displayAlert) {
        if (countDataHistory > 50 && countDataHistory < 100) {
          setDisplayAlert(false);
        }
      }
    }, 5000);
  }, [countDataHistory, displayAlert]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target)
      ) {
        setIsExportDropdownOpen(false);
      }
    };
    if (isExportDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExportDropdownOpen]);

  const closeAlert = () => setDisplayAlert(false);

  return (
    <div className="card-block-activity-item mt-4">
      <section className="flex flex-end w-full mt-2">
        <div className="flex m-auto">
          {countDataHistory === 0 && !isLoading && (
            <Alert
              className="download-alert m-auto py-1.5 modal-alert-red"
              isOpen={displayAlert}
              toggle={closeAlert}
            >
              {t("No data to download")}.
            </Alert>
          )}

          {countDataHistory > 50 && countDataHistory < 100 && (
            <Alert
              className="download-alert m-auto py-1.5 modal-alert-orange"
              isOpen={displayAlert}
              toggle={closeAlert}
            >
              {t(
                "Please note that the number of images may slow down the loading of the PDF",
              )}
              .
            </Alert>
          )}

          {countDataHistory > 100 && !isLoading && (
            <Alert
              className="download-alert m-auto py-1.5 modal-alert-red"
              isOpen={displayAlert}
              toggle={closeAlert}
            >
              {t(
                "To limit the size of the pdf, the number of images must be less than 100",
              )}
              .
            </Alert>
          )}
        </div>
      </section>

      {isLoading ? (
        <div
          className="flex items-center justify-center"
          style={{ backgroundColor: "#F5F5F5", minHeight: "60vh" }}
        >
          <div className="relative">
            <div
              className="w-12 h-12 border-[3px] border-[#00A7A6] border-t-transparent rounded-full animate-spin"
              role="status"
              aria-label="Chargement en cours"
            >
              <span className="sr-only">Chargement...</span>
            </div>
            <div className="absolute inset-0 w-12 h-12 border-[3px] border-[#00A7A6] border-opacity-20 rounded-full animate-ping" />
          </div>
        </div>
      ) : (
        <div>
          {dataHistoryContainer.length > 0 ? (
            <section className="top-table top-table--cols-5">
              <div
                style={{
                  paddingTop: "24px",
                  paddingBottom: "24px",
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div className="create-button-wrapper" ref={exportDropdownRef}>
                  <button
                    className={`create-button flex items-center gap-2 ${disabledDownload || isLoading || !resolveHistoryFlux ? "disabled" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        !disabledDownload &&
                        !isLoading &&
                        resolveHistoryFlux
                      ) {
                        setIsExportDropdownOpen(!isExportDropdownOpen);
                      }
                    }}
                    disabled={
                      disabledDownload || isLoading || !resolveHistoryFlux
                    }
                    aria-busy={isFluxLookupLoading}
                    style={{
                      backgroundColor:
                        disabledDownload || isLoading || !resolveHistoryFlux
                          ? "#d1d5db"
                          : "var(--akanthas-cyan)",
                      color: "#fff",
                      height: "40px",
                    }}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 11 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.33333 8L2 4.66667L2.93333 3.7L4.66667 5.43333V0H6V5.43333L7.73333 3.7L8.66667 4.66667L5.33333 8ZM1.33333 10.6667C0.966667 10.6667 0.652778 10.5361 0.391667 10.275C0.130556 10.0139 0 9.7 0 9.33333V7.33333H1.33333V9.33333H9.33333V7.33333H10.6667V9.33333C10.6667 9.7 10.5361 10.0139 10.275 10.275C10.0139 10.5361 9.7 10.6667 9.33333 10.6667H1.33333Z"
                        fill="white"
                      />
                    </svg>
                    <span className="text-sm font-medium text-white">
                      {t("Export")}
                    </span>
                    {isFluxLookupLoading && (
                      <i
                        className="fas fa-spinner fa-spin"
                        style={{ fontSize: "14px", color: "#6B7280" }}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                  {isExportDropdownOpen &&
                    !disabledDownload &&
                    !isLoading &&
                    resolveHistoryFlux && (
                      <div
                        className="absolute right-0 mt-2 w-40 bg-white border border-gray-100 rounded-md shadow-lg z-10"
                        style={{ minWidth: "160px" }}
                      >
                        <div className="py-1">
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              exportPDF();
                              setIsExportDropdownOpen(false);
                            }}
                            className="block px-4 py-2 text-sm text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700 cursor-pointer"
                          >
                            PDF (.pdf)
                          </span>
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              exportActivityToCSV();
                              setIsExportDropdownOpen(false);
                            }}
                            className="block px-4 py-2 text-sm text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700 cursor-pointer"
                          >
                            CSV (.csv)
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </section>
          ) : null}

          {dataHistoryContainer.length === 0 ? (
            <EmptyState
              title={t("emptyState.noHistory")}
              description={t("emptyState.hintHistory")}
              buttonText={
                hasActiveFilters && onClearFilters
                  ? t("Clear filters")
                  : undefined
              }
              onButtonClick={
                hasActiveFilters && onClearFilters ? onClearFilters : undefined
              }
              buttonIcon={null}
            />
          ) : (
            <Card className="table">
              <div className="table-wrapper table-wrapper--cols-5">
                <ActivityHistoryHeader />
                <article className="content-table">
                  {dataHistoryContainer
                    .slice(0, currentPage * itemsPerPage)
                    .map((timeLine, index) => {
                      return (
                        <ActivityHistoryTimeLineRow
                          key={`${timeLine._id}_${index}`}
                          timeLine={timeLine}
                          index={index}
                          resolveHistoryFlux={resolveHistoryFlux}
                        />
                      );
                    })}
                </article>
              </div>
            </Card>
          )}

          {isModalDownload && !disabledDownload && (
            <PdfDocumentViewer
              countDataHistory={countDataHistory}
              data={dataHistoryExport}
              isModalView={isModalDownload}
              setIsModalView={setIsModalDownload}
              periodStart={periodHistoryStart}
              periodStop={periodHistoryStop}
              setDisplayAlert={setDisplayAlert}
            />
          )}

          {downloadProgress && (
            <LoadingScreen isProgress={true} progress={progress} />
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityHistoryTimeLine;
